import {OrderEdto} from "../../../models/order/order-edto";
import {BookingType} from "../../../models/payment/booking-type";
import {CompositionOverrideLinkerEdto} from "../../../management/composition/composition-override-linker-edto";
import {BookingCalculation} from "../../../_shared/calculation/booking-calculation";

/**
 * DTO glue around the central calculation library (#142): OrderHelper mutates the transmitted
 * OrderEdto/CompositionOverrideLinkerEdto view models, but every formula is delegated to
 * {@link BookingCalculation} — do not re-implement price math here.
 */
export class OrderHelper {
  public static setBusinessCaseBookingTypeValues(order: OrderEdto) {
    let acc: { [key: string]: { amount: number } } = {};
    order.businessCaseBookingTypeValues = order.businessCaseInformations.reduce((accumulator, value) => {
      if (accumulator[value.bookingType] == undefined) accumulator[value.bookingType] = {amount: 0};
      // Route through the defensive sign resolver (#F9) so a missing signIndicator can't NaN-poison the total.
      accumulator[value.bookingType].amount += value.amount * BookingCalculation.resolveSignIndicator(value);
      return accumulator;
    }, acc)
  }

  /** Σ of an order's signed order-level business cases (#142). Structurally typed — see {@link setCompositionPrices}. */
  public static getBusinessCaseBookingTypeTotal(
    order: { businessCaseInformations: { amount: number; signIndicator?: number | null; quantity?: number | null }[] }
  ): number {
    return BookingCalculation.businessCaseTotalSigned(order.businessCaseInformations);
  }

  /** One line's total (#142): `quantity × single price`. Structurally typed for the same reason. */
  public static getCompositionTotal(linker: { quantity: number; price: number }): number {
    return BookingCalculation.totalPrice(linker.quantity, linker.price);
  }

  /**
   * Prices each composition with the canonical distributed unit price (order-level business cases
   * spread pro rata, #142) and stores it as `composition.oPrice`; cleared when it equals the base price.
   *
   * **Typed structurally rather than on `OrderEdto`** (Feature #760, same reasoning as
   * {@link setOrderBookingValues}): the employee „Getränke" view reads `EmployeeSalesOrder` while the orders
   * page still reads `OrderEdto`, and both satisfy this shape. Naming the fields it needs is also the honest
   * statement of what a caller must supply — note it writes `oPrice` onto the ARTICLE, not the line.
   */
  public static setCompositionPrices(order: CompositionPriceSource) {
    let bcTotal = this.getBusinessCaseBookingTypeTotal(order);
    let oTotal = BookingCalculation.orderCompositionTotal(
      order.compositions.map(linker => ({quantity: linker.quantity, singlePrice: linker.price})));
    order.compositions.forEach(linker => {
      linker.composition.oPrice = BookingCalculation.distributedUnitPrice(
        this.getCompositionTotal(linker), oTotal, bcTotal, linker.quantity);
      if (Math.abs(linker.composition.oPrice - linker.composition.price) < Math.pow(10, -6)) linker.composition.oPrice = undefined;
    });
  }

  /**
   * Folds one transaction into its per-transaction-type amounts — the row's „Transaktionstypen" cell.
   *
   * **Typed structurally rather than on `OrderEdto`** (Feature #760): the event-details page and the
   * single-event analysis page now read {@link EventDetailsOrder}, while the employee-sales popup still reads
   * `OrderEdto`. Both satisfy this shape, and naming the fields it actually needs is also the honest
   * statement of what a caller has to supply.
   */
  public static setOrderBookingValues(order: BookingValueSource) {
    let result: { [key: number]: { count: number, amount: number } } = {};
    result[order.bookingType] = {
      count: 1,
      amount: BookingCalculation.orderCompositionTotal(
        order.compositions.map(linker => ({quantity: linker.quantity, singlePrice: linker.price})))
    }
    order.businessCaseInformations.forEach(bci => {
      if (result[bci.bookingType] == undefined) result[bci.bookingType] = {count: 0, amount: 0};
      // Route through the defensive sign resolver (#F9) so a missing signIndicator can't NaN-poison the total.
      result[bci.bookingType].amount += bci.amount * BookingCalculation.resolveSignIndicator(bci);
    })
    order.bookingValues = result;
  }

  public static getOrderTotal(order: { bookingValues: { [key: number]: { count: number, amount: number } } }): number {
    return Object.keys(order.bookingValues).reduce((accumulator, value) => {
      accumulator += order.bookingValues[parseInt(value)].amount
      return accumulator;
    }, 0)
  }
}

/**
 * The minimum an order has to be for {@link OrderHelper.setOrderBookingValues} to fold it: its own
 * transaction type, its priced lines and its order-level business cases — plus the field the fold writes.
 *
 * Satisfied by both wire shapes on purpose; see that method.
 */
export interface CompositionPriceSource {
  compositions: {
    quantity: number;
    price: number;
    composition: { price: number; oPrice?: number };
  }[];
  businessCaseInformations: { amount: number; signIndicator?: number | null; quantity?: number | null }[];
}

export interface BookingValueSource {
  bookingType: BookingType;
  compositions: { quantity: number; price: number }[];
  businessCaseInformations: {
    bookingType: BookingType;
    amount: number;
    signIndicator?: number | null;
    quantity?: number | null;
  }[];
  bookingValues: { [key: number]: { count: number, amount: number } };
}
